
import React, { useMemo, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import SocialButtons from '@/components/SocialButtons.jsx';
import Seo from '@/components/Seo.jsx';

// Données structurées : fiche association pour les moteurs de recherche
const organizationJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Neuvième Continent',
  url: 'https://9continent.fr',
  logo: 'https://9continent.fr/logo-512.jpg',
  description: "Association loi 1901 dédiée au jeu de rôle sur table à Nantes et son agglomération.",
  email: 'contact@9continent.fr',
  telephone: '+33771177599',
  address: {
    '@type': 'PostalAddress',
    streetAddress: "Collectif Plaisance, 4 allée de l'Ille",
    postalCode: '44700',
    addressLocality: 'Orvault',
    addressCountry: 'FR'
  },
  sameAs: [
    'https://discord.com/invite/rPJeDhgMjg',
    'https://neuviemecontinent.overblog.fr/'
  ]
});

// Croix "pixel art" scintillante
const PixelStar = ({ size = 12, className = '', opacity = 1 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" className={className} style={{ opacity }} aria-hidden="true">
    <path d="M4 0h4v4h4v4H8v4H4V8H0V4h4z" fill="#ffffff" />
  </svg>
);

// Icône d20 en filaire
const D20Icon = ({ size = 64, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className={className} aria-hidden="true">
    <polygon points="50,4 89,27 89,73 50,96 11,73 11,27" stroke="#ffffff" strokeWidth="3" />
    <polygon points="50,22 76,66 24,66" stroke="#ffffff" strokeWidth="3" />
    <path d="M50 4 L50 22 M89 27 L76 66 M11 27 L24 66 M50 96 L24 66 M50 96 L76 66 M11 73 L24 66 M89 73 L76 66 M11 27 L50 22 M89 27 L50 22" stroke="#ffffff" strokeWidth="2.5" />
  </svg>
);

// Grille perspective façon vaporwave
const RetroGrid = () => {
  const verticals = [];
  for (let i = 0; i <= 20; i++) {
    const xBottom = i * 50;
    verticals.push(<line key={`v${i}`} x1="500" y1="0" x2={xBottom} y2="300" />);
  }
  const horizontals = [8, 20, 36, 58, 88, 128, 180, 245].map((y) => (
    <line key={`h${y}`} x1="0" y1={y} x2="1000" y2={y} />
  ));
  return (
    <svg
      viewBox="0 0 1000 300"
      preserveAspectRatio="none"
      className="absolute bottom-0 left-0 w-full h-[32%] stroke-white/15"
      style={{ maskImage: 'linear-gradient(to bottom, transparent, black 35%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 35%)' }}
      aria-hidden="true"
    >
      <g strokeWidth="1.5">{verticals}{horizontals}</g>
    </svg>
  );
};

function HomePage() {
  const heroRef = useRef(null);

  // Parallax : les couches du héros défilent à des vitesses différentes
  // à mesure qu'on descend vers la section Discord.
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const skyY = useTransform(scrollYProgress, [0, 1], [0, 140]); // étoiles & dés : lents
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -180]); // logo : remonte plus vite
  const logoOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -90]); // grille : vient à la rencontre

  // Étoiles pixel scintillantes du ciel du héros
  const stars = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        id: i,
        left: Math.random() * 96 + 2,
        top: Math.random() * 70 + 2,
        size: 6 + Math.round(Math.random() * 8),
        delay: Math.random() * 4,
        duration: 2.2 + Math.random() * 2.5
      })),
    []
  );

  return (
    <>
      <Seo
        title="Neuvième Continent - Association de jeu de rôle à Nantes"
        description="Rejoignez Neuvième Continent, l'association nantaise dédiée au jeu de rôle sur table. Parties hebdomadaires, communauté passionnée, tous niveaux bienvenus."
      >
        <script type="application/ld+json">{organizationJsonLd}</script>
      </Seo>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-grow flex flex-col">
          {/* Full-Screen Hero Section */}
          <section
            ref={heroRef}
            className="relative min-h-[100dvh] flex flex-col items-center justify-center overflow-hidden"
            style={{ background: 'linear-gradient(180deg, #6A3E92 0%, #7A4B9E 55%, #5C3486 100%)' }}
          >
            {/* Couche lente : ciel d'étoiles pixel + dés flottants */}
            <motion.div className="absolute inset-0 pointer-events-none" style={{ y: skyY }}>
              {stars.map((s) => (
                <div
                  key={s.id}
                  className="absolute hero-twinkle"
                  style={{
                    left: `${s.left}%`,
                    top: `${s.top}%`,
                    '--twinkle-duration': `${s.duration}s`,
                    '--twinkle-delay': `${s.delay}s`
                  }}
                >
                  <PixelStar size={s.size} />
                </div>
              ))}

              <div className="absolute left-[6%] top-[22%] opacity-25 hero-drift-a">
                <D20Icon size={72} />
              </div>
              <div className="absolute right-[8%] top-[30%] opacity-20 hero-drift-b">
                <D20Icon size={52} />
              </div>
              <div className="absolute left-[14%] bottom-[18%] opacity-15 hidden md:block hero-drift-c">
                <D20Icon size={44} />
              </div>
            </motion.div>

            {/* Couche rapide : grille rétro à l'horizon */}
            <motion.div className="absolute inset-0 pointer-events-none" style={{ y: gridY }}>
              <RetroGrid />
            </motion.div>

            {/* Logo + halo holographique (couche premier plan) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              style={{ y: logoY, opacity: logoOpacity, scale: logoScale }}
              className="relative z-10 flex flex-col items-center justify-center px-4 w-full max-w-7xl mx-auto"
            >
              {/* Halo holographique. Il tournait en continu, ce qui obligeait le
                  navigateur à recalculer un flou de 60 px à chaque image. Le
                  dégradé est désormais figé (rasterisé une seule fois) et seule
                  son opacité respire, ce qui est composé par le GPU. */}
              <div
                className="absolute w-[70vmin] h-[70vmin] rounded-full pointer-events-none hero-halo"
                style={{
                  background:
                    'conic-gradient(from 0deg, rgba(239,80,157,0.25), rgba(255,255,255,0.12), rgba(139,69,214,0.3), rgba(242,114,119,0.2), rgba(239,80,157,0.25))',
                  filter: 'blur(60px)'
                }}
                aria-hidden="true"
              />

              <img
                src="/logo-hero.svg"
                alt="Neuvième Continent Logo"
                width="842"
                height="595"
                fetchpriority="high"
                decoding="async"
                className="relative w-[55%] md:w-[70%] lg:w-[78%] h-auto object-contain drop-shadow-2xl hero-float"
              />

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="relative font-pixel text-white/90 text-base sm:text-lg md:text-2xl tracking-widest uppercase mt-6 flex flex-wrap items-center justify-center gap-3 text-center px-2"
              >
                <PixelStar size={10} opacity={0.8} />
                Association de jeu de rôle — Nantes
                <PixelStar size={10} opacity={0.8} />
              </motion.p>
            </motion.div>

            {/* Indicateur de scroll pixel */}
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none hero-scroll-hint"
              aria-hidden="true"
            >
              <svg width="28" height="18" viewBox="0 0 28 18">
                <path d="M0 0h6v6h-6z M22 0h6v6h-6z M6 6h6v6H6z M16 6h6v6h-6z M11 12h6v6h-6z" fill="#ffffff" opacity="0.85" />
              </svg>
            </div>
          </section>

          {/* Discord CTA Section — épurée */}
          <section
            className="py-28 px-4 sm:px-6 lg:px-8 relative z-20 overflow-hidden"
            style={{ background: 'linear-gradient(180deg, #5C3486 0%, #7A4B9E 100%)' }}
          >
            {/* Lueurs d'ambiance discrètes */}
            <div className="absolute top-[-20%] left-[-10%] w-[45%] h-[60%] rounded-full bg-[#EF509D]/10 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[45%] h-[60%] rounded-full bg-[#E8706A]/10 blur-[120px] pointer-events-none" />

            <div className="max-w-3xl mx-auto text-center relative">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div
                  className="flex justify-center mb-8 hero-pulse"
                >
                  <PixelStar size={14} opacity={0.8} />
                </div>

                <h2 className="font-elegant text-4xl md:text-5xl font-bold text-white mb-6 text-balance">
                  Rejoins-nous sur Discord
                </h2>
                <div className="w-16 h-1 bg-white/30 mx-auto rounded-full mb-8"></div>
                <p className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto text-balance">
                  On utilise discord pour discuter, organiser des parties, partager des ressources et parler de la vie associative. Si tu souhaites nous rejoindre, suis les instructions du discord ou envoie-nous un email !
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="flex justify-center mt-10"
              >
                <SocialButtons />
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default HomePage;
